'use client'

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import {
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  FolderIcon,
  SparklesIcon,
  DocumentTextIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'

interface UserProfileMenuProps {
  variant?: 'light' | 'dark'
}

export default function UserProfileMenu({ variant = 'light' }: UserProfileMenuProps) {
  const { user, signOut } = useAuth()
  const { userRole } = useUserRole()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSignOut = async () => {
    await signOut()
    setIsOpen(false)
    // Redirect is handled automatically by AuthContext
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  const getUserInitial = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return 'U'
  }

  const getUserRole = () => {
    return userRole?.role || 'user'
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 p-2 rounded-lg transition-colors focus-ring ${
          variant === 'dark'
            ? 'hover:bg-white/10'
            : 'hover:bg-gray-100'
        }`}
        aria-label="User menu"
      >
        <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-white">
            {getUserInitial()}
          </span>
        </div>
        {variant === 'light' && (
          <div className="hidden md:block text-left">
            <div className="text-sm font-medium text-gray-900 truncate max-w-32">
              {user?.email}
            </div>
            <div className="text-xs text-gray-500">
              {getUserRole()}
            </div>
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="py-1">
            <button
              onClick={() => handleNavigate('/projects')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FolderIcon className="w-4 h-4 mr-3 text-gray-400" />
              Projects
            </button>

            <button
              onClick={() => handleNavigate('/clients')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <BuildingOffice2Icon className="w-4 h-4 mr-3 text-gray-400" />
              Clients
            </button>

            <button
              onClick={() => handleNavigate('/prompts')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <SparklesIcon className="w-4 h-4 mr-3 text-gray-400" />
              Prompts
            </button>

            <button
              onClick={() => handleNavigate('/docs')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <DocumentTextIcon className="w-4 h-4 mr-3 text-gray-400" />
              Docs
            </button>

            <button
              onClick={() => handleNavigate('/profile')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <UserIcon className="w-4 h-4 mr-3 text-gray-400" />
              Profile
            </button>

            {getUserRole() === 'admin' && (
              <button
                onClick={() => handleNavigate('/users')}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <UsersIcon className="w-4 h-4 mr-3 text-gray-400" />
                User Management
              </button>
            )}

            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3 text-gray-400" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
